/**
 * Created by Jie Gao on 2019-04-16.
 */
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {IReelStartSpinData} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelController";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {DragonTribeParsedGameData} from "../server/data/DragonTribeParsedGameData";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {DragonTribeParsedInitData} from "../server/data/DragonTribeParsedInitData";
import {ISymbolStateConfig, StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {AvalancheReelController} from "@nolimitcity/slot-game/bin/game/reel/avalanchereel/AvalancheReelController";
import {FreespinFeatureEvent} from "../winpresentation/fswildmeter/events/FreespinFeatureEvent";
import {DTReelEvents} from "./DTReelEvents";
import {DTReelSymbolM} from "../symbols/DTReelSymbolM";
import {DTReelPartAnimator} from "./DTReelPartAnimator";
import {TimelineLite} from "gsap";

export class DTReelController extends AvalancheReelController {

    public static readonly SYM_SIZE:{ [reelSize:number]:{ height:number, padding:number } } = {
        1 : {height : 510, padding : 0},
        3 : {height : 170, padding : 0},
        4 : {height : 127.5, padding : 0},
        5 : {height : 102, padding : 0},
        6 : {height : 85, padding : 0}
    };

    private _reelSize:number;
    private _allWildSyms:string[] = [];
    private _wildSymbols:string[] = [];
    private _hasWin:boolean = false;
    private _hadWin:boolean = false;

    public get targetPositions():number[] {
        return (<DTReelPartAnimator>this._reelPartAnimator).targetPositions;
    }

    constructor(reelId:number) {
        super(reelId);
    }

    protected addEventListeners():void {
        super.addEventListeners();
        EventHandler.addEventListener(this, DTReelEvents.HIDE_REEL, (event:GameEvent) => this.hideReel(event.params[0]));
        EventHandler.addEventListener(this, DTReelEvents.SHOW_REEL, (event:GameEvent) => this.showReel());
        EventHandler.addEventListener(this, FreespinFeatureEvent.PATCH_WILD_ON_M, (event:GameEvent) => this.onSetWildSymbol(event.params[0], event.params[1]));
        EventHandler.addEventListener(this, FreespinFeatureEvent.HIDE_METER, (event:GameEvent) => this.resetWildData());
    }

    protected onInitDataParsed(data:DragonTribeParsedInitData):void {
        this.parseReelSize(data.gameAreaSize[this._reelId]);
        super.onInitDataParsed(data);
        if(data.isRestoreState && this.isNextAvalanche()) {
            this._hasWin = true;
        }
    }

    public onGameDataParsed(data:DragonTribeParsedGameData):void {
        this.parseReelSize(data.gameAreaSize[this._reelId]);
        super.onGameDataParsed(data);

        // Change the top positions to be true so it removes the X symbols when avalanche.
        this._hadWin = this._hasWin;
        this._hasWin = this._allWinPositions.includes(true);
        const numOffset:number = 6 - data.actualReelSize[this._reelId];
        for(let i:number = 0; i < numOffset; i++) {
            if(this._hasWin) {
                this._allWinPositions[i] = true;
            }
            if(this._hadWin) {
                this._newSymbols.unshift("X");
            }

        }
    }

    private parseReelSize(gameAreaSize:number):void {
        this._reelSize = gameAreaSize;
        this.initAnimator();
    }

    protected initAnimator():void {
        this._reelPartAnimator.symWidth = GameConfig.instance.SYMBOL_WIDTH;
        this._reelPartAnimator.symBottomPadding = DTReelController.SYM_SIZE[this._reelSize].padding;
        this._reelPartAnimator.symHeight = DTReelController.SYM_SIZE[this._reelSize].height;
    }

    public addExpandingSymbols(newSymName:string, mysterySymbolSize:number, positionId:number, reelSize:number):void {
        this.parseReelSize(reelSize + mysterySymbolSize - 1);
        const symbolHeight:number = DTReelController.SYM_SIZE[this._reelSize].height;
        const symbolStep:number = symbolHeight;
        const mysterySymbolHeight:number = symbolHeight * mysterySymbolSize;
        const mysterySymbol:StateReelSymbol = this.findSymbol(positionId)!;
        const mysterySymbolIdInStopSymbols:number = positionId;

        // Replace symbol and add more symbols
        for(let i:number = 0; i < mysterySymbolSize; i++) {
            const newSymbol:StateReelSymbol = this.addNewSymbol(newSymName);
            // no need to update stack because it can never be W as revealed symbol.
            if(i === 0) {
                this.allSymbols.splice(mysterySymbolIdInStopSymbols, 1, newSymbol);
            } else {
                this.allSymbols.splice(mysterySymbolIdInStopSymbols + 1, 0, newSymbol);
                const removedSymbol:StateReelSymbol = this.allSymbols.shift()!;
                this.removeReplacedSymbol(removedSymbol);
            }
            newSymbol.position.set(0, mysterySymbol.y - mysterySymbolHeight / 2 + i * symbolStep + symbolHeight / 2);
        }

        this._reelView.addChild(mysterySymbol); // move to top
        (<DTReelPartAnimator>this._reelPartAnimator).setAllSymbols(this.allSymbols.map(s => s!));
        (<DTReelPartAnimator>this._reelPartAnimator).resetSize(symbolHeight, true);
    }

    public hideReel(reelId:number):void {
        if(reelId === this.reelId) {
            this.view.visible = false;
        }
    }

    public showReel():void {
        this.view.visible = true;
    }

    public onSetWildSymbol(wildSymbols:string[], witAnimation:boolean):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        this._wildSymbols = wildSymbols;
        this._allWildSyms = this._allWildSyms.concat(wildSymbols);
        if(this._reelInitialized) {
            for(let i:number = 0; i < this.allSymbols.length; i++) {
                const symbol:DTReelSymbolM = <DTReelSymbolM>this.allSymbols[i];
                if(symbol && (this._wildSymbols.length > 0 && this._wildSymbols.indexOf(symbol.symName) > -1)) {
                    tl.add(symbol.animateWildPatch(witAnimation), 0);
                }
            }
        }
        return tl;
    }

    public getRealSymId(symbol:StateReelSymbol):number {
        const stopSymbolIndex:number = this.allSymbols.indexOf(symbol);
        if(stopSymbolIndex === -1) {
            debugger;
            Logger.logDev(`PPReelController.getRealSymId(), symbol doesn't exist in reel!`);
        }
        return stopSymbolIndex;
    }

    public removeReplacedSymbol(replacedSymbol:StateReelSymbol):void {
        this._reelView.removeChild(replacedSymbol);
        this._reelPartCreator.deleteSymbol(replacedSymbol);
    }

    public showStackWild(reelSize:number):void {
        const step:number = DTReelController.SYM_SIZE[reelSize].height;
        const topSymId:number = 6 - reelSize;

        const stopSymbols:StateReelSymbol[] = [];
        for(let j:number = 0; j < 6; j++) {
            if(j < topSymId) {
                this.allSymbols[j]!.replaceWith("X", this._reelId);
                stopSymbols.push(this.allSymbols[j]!);
            } else {
                const id:number = j - topSymId;
                this.removeSymbol(this.findSymbol(j)!);
                const newSym:StateReelSymbol = this.addNewSymbol(`W0*${id}`);
                this.allSymbols[j] = newSym;
                this.allSymbols[j]!.symHeight = step;
                this.allSymbols[j]!.position.y = (<DTReelPartAnimator>this._reelPartAnimator).targetPositions[j];
                this.updateStackedSymbols(this.allSymbols[j]!, (id == 0) ? null : this.allSymbols[j - 1]!, 1, true);
                stopSymbols.push(this.allSymbols[j]!);
            }
        }
        this._reelPartAnimator.setAllSymbols(stopSymbols);
    }

    protected addNewSymbol(symName:string, state?:ISymbolStateConfig):StateReelSymbol {
        const symbol:StateReelSymbol = super.addNewSymbol(symName, state);
        if(this._allWildSyms.indexOf(symbol.symName) > -1) {
            (<DTReelSymbolM>symbol).isWild = true;
        } else {
            (<DTReelSymbolM>symbol).isWild = false;
        }
        return symbol;
    }

    private resetWildData():void {
        this._allWildSyms = [];
    }

    protected setStartSpinData(data:IReelStartSpinData):void {
        super.setStartSpinData(data);
        if(!this.isAvalanche()) {
            (<DTReelPartAnimator>this._reelPartAnimator).resetSize(DTReelController.SYM_SIZE[4].height, false);
        }
    }
}